/* eslint-disable no-unused-vars, react-hooks/exhaustive-deps */
// frontend/src/components/TaxiFareCalculator.js
import React, { useState, useCallback, useEffect } from 'react';
import {
  useJsApiLoader,
  GoogleMap,
  Autocomplete,
  DirectionsRenderer
} from '@react-google-maps/api';
import '../styles/TaxiFareCalculator.css';
import { Accordion, Modal, Button, Alert, Spinner } from 'react-bootstrap';
import 'bootstrap/dist/css/bootstrap.min.css';
import 'bootstrap/dist/js/bootstrap.bundle.min';
import LeadForm from './LeadForm';
import axios from 'axios';

const containerStyle = {
  width: '100vw',
  height: '100vh',
  position: 'absolute',
  top: 0,
  left: 0
};
const center = { lat: 52.3555, lng: -1.1743 };

// Define libraries array as a constant outside the component
const GOOGLE_MAPS_LIBRARIES = ['places'];

// Mobile fallback component without Google Maps autocomplete
const MobileFareCalculator = ({
  startLocation, setStartLocation,
  endLocation, setEndLocation,
  handleCalculateRoute, fetchCurrentLocation,
  isCalculated, distance, duration, fare,
  licensingBody, setShowLeadModal, resetAll,
  showManualMileageDialog, detectingCouncil, authorityDetectionTimeout
}) => {
  if (!isCalculated) {
    return (
      <>
        {/* Start */}
        <div className="mb-3">
          <label>Start Location:</label>
          <div className="d-flex">
            <input
              className="form-control flex-grow-1"
              placeholder="Enter start location"
              value={startLocation}
              onChange={e => setStartLocation(e.target.value)}
            />
            {startLocation
              ? <button className="btn btn-outline-secondary ms-2" onClick={() => setStartLocation('')}>Clear</button>
              : <button className="btn btn-outline-primary ms-2" onClick={fetchCurrentLocation}>Current Location</button>
            }
          </div>
        </div>

        {/* End */}
        <div className="mb-3">
          <label>End Location:</label>
          <div className="d-flex">
            <input
              className="form-control flex-grow-1"
              placeholder="Enter end location"
              value={endLocation}
              onChange={e => setEndLocation(e.target.value)}
            />
            {endLocation && (
              <button className="btn btn-outline-secondary ms-2" onClick={() => setEndLocation('')}>Clear</button>
            )}
          </div>
        </div>

        {/* Detected Council */}
        {detectingCouncil && !authorityDetectionTimeout ? (
          <Alert variant="info" className="mb-3">
            <Spinner size="sm" className="me-2" />
            Detecting licensing authority...
          </Alert>
        ) : authorityDetectionTimeout || (!licensingBody && startLocation) ? (
          <Alert variant="warning" className="mb-3">
            <strong>Authority NOT Detected</strong> - Using Default values to give estimate
          </Alert>
        ) : licensingBody ? (
          <Alert variant="success" className="mb-3">
            <strong>Detected:</strong> {licensingBody.name} ({licensingBody.code})
            <br />
            <small>Based on start location postcode</small>
          </Alert>
        ) : null}

        {/* Actions */}
        <div className="d-grid gap-2 mb-4">
          <button className="btn btn-success" onClick={handleCalculateRoute}>
            Calculate
          </button>
        </div>
      </>
    );
  } else {
    return (
      <>
        <h3 className="text-center mb-3">Fare Calculation Estimate</h3>
        
        {licensingBody && (
          <Alert variant="info" className="mb-3 text-center">
            <strong>{licensingBody.fullName || licensingBody.name}</strong>
          </Alert>
        )}
        
        <p className="text-center"><strong>{startLocation}</strong> → <strong>{endLocation}</strong></p>
        <p className="text-center"><strong>Distance:</strong> {distance.toFixed(1)} miles</p>
        <p className="text-center"><strong>Duration:</strong> {Math.round(duration)} minutes</p>

        {fare && fare.length > 0 && fare.map((tariffFare, idx) => (
          <p className="text-center" key={idx}>
            <strong>{tariffFare.name}:</strong> £{tariffFare.price}
          </p>
        ))}

        <div className="d-grid gap-2 my-4">
          <button className="btn btn-primary" onClick={() => setShowLeadModal(true)}>
            Book Taxi Online
          </button>
          <button
            className="btn btn-success"
            onClick={() =>
              window.open(
                `https://www.google.com/maps/dir/?api=1&origin=${encodeURIComponent(startLocation)}&destination=${encodeURIComponent(endLocation)}&travelmode=driving`,
                '_blank'
              )
            }
          >
            Open in Google Maps
          </button>
          <button className="btn btn-secondary" onClick={resetAll}>
            Reset
          </button>
        </div>

        <Accordion flush>
          <Accordion.Item eventKey="0">
            <Accordion.Header>Tariffs Definition</Accordion.Header>
            <Accordion.Body>
              {licensingBody && licensingBody.tariffs && licensingBody.tariffs.map((t, idx) => (
                <div key={idx} className="mb-2">
                  <strong>{t.name}:</strong> {t.description || `First mile: £${t.firstMile.toFixed(2)}, Subsequent: £${t.subsequentMile.toFixed(2)}`}
                </div>
              ))}
            </Accordion.Body>
          </Accordion.Item>
          <Accordion.Item eventKey="1">
            <Accordion.Header>Disclaimer</Accordion.Header>
            <Accordion.Body>
              The fares calculated by this tool are estimates based on the shortest driving distance and standard
              tariffs provided by the licensing authority. Actual fares may vary due to traffic, detours, waiting times,
              tolls, or other surcharges. Taxi-Local does not accept any responsibility for Fares or Payments to the Operator.
            </Accordion.Body>
          </Accordion.Item>
        </Accordion>
      </>
    );
  }
};

export default function FareCalculator() {
  // State
  const [startLocation, setStartLocation]       = useState('');
  const [endLocation, setEndLocation]           = useState('');
  const [licensingBody, setLicensingBody]       = useState(null);
  const [detectingCouncil, setDetectingCouncil] = useState(false);
  const [authorityDetectionTimeout, setAuthorityDetectionTimeout] = useState(false);
  const [directionsResponse, setDirectionsResponse] = useState(null);
  const [fare, setFare]                         = useState(null);
  const [distance, setDistance]                 = useState(null);
  const [duration, setDuration]                 = useState(null);
  const [isCalculated, setIsCalculated]         = useState(false);
  const [showLeadModal, setShowLeadModal]       = useState(false);
  const [mapLoadTimeout, setMapLoadTimeout]     = useState(false);
  const [isMobile, setIsMobile]                 = useState(false);
  const [showMileageModal, setShowMileageModal]  = useState(false);
  const [manualMileage, setManualMileage]        = useState('');

  // Detect mobile device
  useEffect(() => {
    const checkMobile = () => {
      const isMobileDevice = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      setIsMobile(isMobileDevice);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries: GOOGLE_MAPS_LIBRARIES
  });

  // Set timeout for map loading on mobile
  useEffect(() => {
    if (isMobile) {
      const timeout = setTimeout(() => {
        if (!isLoaded && !loadError) {
          setMapLoadTimeout(true);
        }
      }, 10000);

      return () => clearTimeout(timeout);
    }
  }, [isMobile, isLoaded, loadError]);

  // Extract postcode from address
  const extractPostcode = (address) => {
    if (!address) return null;

    const normalizedAddress = String(address).toUpperCase();

    // UK postcode pattern - more comprehensive
    const postcodePattern = /([A-Z]{1,2}[0-9]{1,2}[A-Z]?)(?:\s*[0-9][A-Z]{2})?/;
    const match = normalizedAddress.match(postcodePattern);

    if (match) {
      return match[1].trim(); // Return the outward code (e.g., "EX12" from "EX12 4AB")
    }

    return null;
  };

  // Fetch licensing body by postcode
  const fetchLicensingBodyByPostcode = useCallback(async (postcode) => {
    if (!postcode) return null;

    const outwardCode = postcode.trim().toUpperCase();
    if (!outwardCode) return null;

    // Set timeout to show message after 5 seconds
    const timeoutMessageId = setTimeout(() => {
      setAuthorityDetectionTimeout(true);
    }, 5000); // 5 second timeout for showing message

    // Set timeout to prevent infinite loading
    const timeoutId = setTimeout(() => {
      setDetectingCouncil(false);
      console.warn('Licensing body detection timed out');
    }, 10000); // 10 second timeout

    try {
      setDetectingCouncil(true);
      setAuthorityDetectionTimeout(false);
      
      // Create axios request with timeout
      const response = await axios.get(`/licensing-bodies/postcode/${outwardCode}`, {
        timeout: 8000 // 8 second timeout for the request
      });
      
      clearTimeout(timeoutId);
      clearTimeout(timeoutMessageId);
      setLicensingBody(response.data);
      setDetectingCouncil(false);
      setAuthorityDetectionTimeout(false);
      return response.data;
    } catch (error) {
      clearTimeout(timeoutId);
      clearTimeout(timeoutMessageId);
      console.error('Error fetching licensing body:', error);
      
      // If it's a timeout or network error, set detecting to false immediately
      if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
        setDetectingCouncil(false);
        setAuthorityDetectionTimeout(true);
        // Try to get default licensing body
        try {
          const defaultResponse = await axios.get('/licensing-bodies/code/DEFAULT', {
            timeout: 5000
          });
          setLicensingBody(defaultResponse.data);
          return defaultResponse.data;
        } catch (defaultError) {
          console.error('Error fetching default licensing body:', defaultError);
          setLicensingBody(null);
          return null;
        }
      }
      
      // Try to get default licensing body
      try {
        const defaultResponse = await axios.get('/licensing-bodies/code/DEFAULT', {
          timeout: 5000
        });
        setLicensingBody(defaultResponse.data);
        setDetectingCouncil(false);
        setAuthorityDetectionTimeout(true); // Show message even with default
        return defaultResponse.data;
      } catch (defaultError) {
        console.error('Error fetching default licensing body:', defaultError);
        setLicensingBody(null);
        setDetectingCouncil(false);
        setAuthorityDetectionTimeout(true);
        return null;
      }
    }
  }, []);

  // Auto-detect licensing body when start location changes
  useEffect(() => {
    if (startLocation && !isCalculated) {
      const postcode = extractPostcode(startLocation);
      if (postcode) {
        setAuthorityDetectionTimeout(false); // Reset timeout state
        fetchLicensingBodyByPostcode(postcode);
      } else {
        // If no postcode found, show timeout message
        setAuthorityDetectionTimeout(true);
        setDetectingCouncil(false);
      }
    } else {
      // Reset states when start location is cleared
      setAuthorityDetectionTimeout(false);
      setDetectingCouncil(false);
    }
  }, [startLocation, isCalculated, fetchLicensingBodyByPostcode]);

  // Calculate fare from tariffs
  const calculateFare = useCallback((miles, tariffs) => {
    if (!tariffs || tariffs.length === 0) return [];
    
    return tariffs
      .filter(t => t.active !== false)
      .map(t => {
        const extra = miles > 1 ? (miles - 1) * t.subsequentMile : 0;
        return {
          name: t.name,
          description: t.description,
          price: Math.round(t.firstMile + extra)
        };
      });
  }, []);

  // Show manual mileage input dialog
  const showManualMileageDialog = useCallback(() => {
    const estimatedMiles = calculateEstimatedDistance(startLocation, endLocation);
    setManualMileage(estimatedMiles.toString());
    setShowMileageModal(true);
  }, [startLocation, endLocation]);

  const handleCalculateRoute = useCallback(async () => {
    if (!startLocation || !endLocation) {
      return alert('Please enter both start and end locations.');
    }

    // Ensure we have a licensing body
    if (!licensingBody) {
      const postcode = extractPostcode(startLocation);
      if (postcode) {
        const body = await fetchLicensingBodyByPostcode(postcode);
        if (!body) {
          alert('Unable to determine licensing authority. Please try again or enter a more specific location.');
          return;
        }
      } else {
        alert('Unable to detect postcode from start location. Please enter a more specific address.');
        return;
      }
    }

    // Check if Google Maps API is available
    if (!window.google || !window.google.maps || !window.google.maps.DirectionsService) {
      showManualMileageDialog();
      return;
    }

    const svc = new window.google.maps.DirectionsService();
    svc.route({
      origin: startLocation,
      destination: endLocation,
      travelMode: window.google.maps.TravelMode.DRIVING
    }, (resp, status) => {
      if (status === 'OK') {
        const leg = resp.routes[0].legs[0];
        const miles = leg.distance.value / 1609.34;
        const mins = leg.duration.value / 60;
        setDirectionsResponse(resp);
        setDistance(miles);
        setDuration(mins);
        setFare(calculateFare(miles, licensingBody.tariffs));
        setIsCalculated(true);
      } else {
        alert('Directions request failed: ' + status);
      }
    });
  }, [startLocation, endLocation, licensingBody, calculateFare, showManualMileageDialog, fetchLicensingBodyByPostcode]);

  // Handle manual mileage calculation
  const handleManualMileageCalculation = () => {
    const miles = parseFloat(manualMileage);
    if (!isNaN(miles) && miles > 0) {
      const estimatedMinutes = miles * 2;
      setDistance(miles);
      setDuration(estimatedMinutes);
      setFare(calculateFare(miles, licensingBody?.tariffs || []));
      setIsCalculated(true);
      setShowMileageModal(false);
    } else {
      alert('Please enter a valid distance in miles.');
    }
  };

  // Enhanced distance estimation
  const calculateEstimatedDistance = (start, end) => {
    const startLower = start.toLowerCase();
    const endLower = end.toLowerCase();
    
    const cityDistances = {
      'london': { lat: 51.5074, lng: -0.1278 },
      'manchester': { lat: 53.4808, lng: -2.2426 },
      'birmingham': { lat: 52.4862, lng: -1.8904 },
      'liverpool': { lat: 53.4084, lng: -2.9916 },
      'leeds': { lat: 53.8008, lng: -1.5491 },
      'sheffield': { lat: 53.3811, lng: -1.4701 },
      'bristol': { lat: 51.4545, lng: -2.5879 },
      'newcastle': { lat: 54.9783, lng: -1.6178 },
      'exeter': { lat: 50.7184, lng: -3.5339 },
      'plymouth': { lat: 50.3755, lng: -4.1427 },
      'southampton': { lat: 50.9097, lng: -1.4044 },
      'portsmouth': { lat: 50.8198, lng: -1.0880 },
      'bournemouth': { lat: 50.7192, lng: -1.8808 },
      'poole': { lat: 50.7150, lng: -1.9873 },
      'dorchester': { lat: 50.7155, lng: -2.4373 }
    };
    
    let startCity = null;
    let endCity = null;
    
    for (const [key, coords] of Object.entries(cityDistances)) {
      if (startLower.includes(key)) startCity = coords;
      if (endLower.includes(key)) endCity = coords;
    }
    
    if (startCity && endCity) {
      return calculateHaversineDistance(startCity.lat, startCity.lng, endCity.lat, endCity.lng);
    }
    
    const postcodePattern = /[A-Z]{1,2}[0-9]{1,2}/i;
    if (postcodePattern.test(start) || postcodePattern.test(end)) {
      return 8;
    }
    
    return 8;
  };

  const calculateHaversineDistance = (lat1, lon1, lat2, lon2) => {
    const R = 3959;
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
              Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
              Math.sin(dLon/2) * Math.sin(dLon/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return Math.round(R * c * 10) / 10;
  };

  const resetAll = useCallback(() => {
    setStartLocation('');
    setEndLocation('');
    setDirectionsResponse(null);
    setFare(null);
    setDistance(null);
    setDuration(null);
    setIsCalculated(false);
    setLicensingBody(null);
  }, []);

  const fetchCurrentLocation = useCallback(() => {
    if (!navigator.geolocation) {
      return alert('Geolocation not supported.');
    }
    
    navigator.geolocation.getCurrentPosition(
      ({ coords }) => {
        if (!window.google || !window.google.maps || !window.google.maps.Geocoder) {
          setStartLocation(`Current Location (${coords.latitude.toFixed(4)}, ${coords.longitude.toFixed(4)})`);
          return;
        }
        
        const loc = new window.google.maps.LatLng(coords.latitude, coords.longitude);
        new window.google.maps.Geocoder().geocode({ location: loc }, (results, status) => {
          if (status === window.google.maps.GeocoderStatus.OK && results[0]) {
            let addr = results[0].formatted_address.replace(/^[A-Z0-9+]+\s*/, '');
            setStartLocation(addr);
          } else {
            alert('No address found.');
          }
        });
      },
      () => alert('Please enable location services.')
    );
  }, []);

  // Loading / error handling
  if (loadError) {
    return (
      <div className="map-container" style={{ backgroundColor: '#F5F5F5', padding: '2rem' }}>
        <div className="text-center p-3 mb-4" style={{ backgroundColor: 'yellow' }}>
          <img src="/logo192.png" alt="Taxi-Local.uk Logo" style={{ maxWidth: 150 }} />
        </div>
        <h2 className="text-center mb-4">Taxi Fare Calculator v4.0</h2>
        <div className="alert alert-warning">
          <h4>⚠️ Google Maps Loading Issue</h4>
          <p>We're experiencing issues loading Google Maps. You can still use the fare calculator with manual address entry.</p>
          <button className="btn btn-primary" onClick={() => window.location.reload()}>
            Try Again
          </button>
        </div>
        <MobileFareCalculator 
          startLocation={startLocation}
          setStartLocation={setStartLocation}
          endLocation={endLocation}
          setEndLocation={setEndLocation}
          handleCalculateRoute={handleCalculateRoute}
          fetchCurrentLocation={fetchCurrentLocation}
          isCalculated={isCalculated}
          distance={distance}
          duration={duration}
          fare={fare}
          licensingBody={licensingBody}
          setShowLeadModal={setShowLeadModal}
          resetAll={resetAll}
          showManualMileageDialog={showManualMileageDialog}
          detectingCouncil={detectingCouncil}
          authorityDetectionTimeout={authorityDetectionTimeout}
        />
      </div>
    );
  }

  if (!isLoaded && !mapLoadTimeout) {
    return (
      <div className="map-container" style={{ backgroundColor: '#F5F5F5', padding: '2rem' }}>
        <div className="text-center p-3 mb-4" style={{ backgroundColor: 'yellow' }}>
          <img src="/logo192.png" alt="Taxi-Local.uk Logo" style={{ maxWidth: 150 }} />
        </div>
        <h2 className="text-center mb-4">Taxi Fare Calculator v4.0</h2>
        <div className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-3">Loading map...</p>
          {isMobile && (
            <p className="text-muted">This may take a moment on mobile devices</p>
          )}
        </div>
      </div>
    );
  }

  if (mapLoadTimeout && !isLoaded) {
    return (
      <div className="map-container" style={{ backgroundColor: '#F5F5F5', padding: '2rem' }}>
        <div className="text-center p-3 mb-4" style={{ backgroundColor: 'yellow' }}>
          <img src="/logo192.png" alt="Taxi-Local.uk Logo" style={{ maxWidth: 150 }} />
        </div>
        <h2 className="text-center mb-4">Taxi Fare Calculator v4.0</h2>
        <div className="alert alert-info">
          <h4>📱 Mobile Mode</h4>
          <p>Google Maps is taking longer to load. You can still calculate fares using manual address entry.</p>
        </div>
        <MobileFareCalculator 
          startLocation={startLocation}
          setStartLocation={setStartLocation}
          endLocation={endLocation}
          setEndLocation={setEndLocation}
          handleCalculateRoute={handleCalculateRoute}
          fetchCurrentLocation={fetchCurrentLocation}
          isCalculated={isCalculated}
          distance={distance}
          duration={duration}
          fare={fare}
          licensingBody={licensingBody}
          setShowLeadModal={setShowLeadModal}
          resetAll={resetAll}
          showManualMileageDialog={showManualMileageDialog}
          detectingCouncil={detectingCouncil}
          authorityDetectionTimeout={authorityDetectionTimeout}
        />
      </div>
    );
  }

  // Main render
  return (
    <div className="map-container" style={{ backgroundColor: '#F5F5F5' }}>
      {!isCalculated ? (
        <>
          <div className="text-center p-3 mb-4" style={{ backgroundColor: 'yellow' }}>
            <img src="/logo192.png" alt="Taxi-Local.uk Logo" style={{ maxWidth: 150 }} />
          </div>
          <h2 className="text-center mb-4">Taxi Fare Calculator v4.0</h2>

          {/* Start */}
          <div className="mb-3">
            <label>Start Location:</label>
            <div className="d-flex">
              <Autocomplete
                onPlaceChanged={() => setStartLocation(document.getElementById('start').value)}
                options={{ componentRestrictions: { country: 'uk' } }}
              >
                <input
                  id="start"
                  className="form-control flex-grow-1"
                  placeholder="Enter start location"
                  value={startLocation}
                  onChange={e => setStartLocation(e.target.value)}
                />
              </Autocomplete>
              {startLocation
                ? <button className="btn btn-outline-secondary ms-2" onClick={() => setStartLocation('')}>Clear</button>
                : <button className="btn btn-outline-primary ms-2" onClick={fetchCurrentLocation}>Current Location</button>
              }
            </div>
          </div>

          {/* End */}
          <div className="mb-3">
            <label>End Location:</label>
            <div className="d-flex">
              <Autocomplete
                onPlaceChanged={() => setEndLocation(document.getElementById('end').value)}
                options={{ componentRestrictions: { country: 'uk' } }}
              >
                <input
                  id="end"
                  className="form-control flex-grow-1"
                  placeholder="Enter end location"
                  value={endLocation}
                  onChange={e => setEndLocation(e.target.value)}
                />
              </Autocomplete>
              {endLocation && (
                <button className="btn btn-outline-secondary ms-2" onClick={() => setEndLocation('')}>Clear</button>
              )}
            </div>
          </div>

          {/* Detected Council */}
          {detectingCouncil ? (
            <Alert variant="info" className="mb-3">
              <Spinner size="sm" className="me-2" />
              Detecting licensing authority...
            </Alert>
          ) : licensingBody ? (
            <Alert variant="success" className="mb-3">
              <strong>Detected:</strong> {licensingBody.name} ({licensingBody.code})
              <br />
              <small>Based on start location postcode</small>
              {licensingBody.postcodes && licensingBody.postcodes.length > 0 && (
                <>
                  <br />
                  <small className="text-muted">Covers: {licensingBody.postcodes.slice(0, 5).join(', ')}{licensingBody.postcodes.length > 5 ? '...' : ''}</small>
                </>
              )}
            </Alert>
          ) : startLocation && (
            <Alert variant="warning" className="mb-3">
              Unable to detect licensing authority. Using default tariffs.
            </Alert>
          )}

          {/* Actions */}
          <div className="d-grid gap-2 mb-4">
            <button className="btn btn-success" onClick={handleCalculateRoute}>
              Calculate
            </button>
          </div>
        </>
      ) : (
        <>
          <div className="text-center p-3 mb-4" style={{ backgroundColor: 'yellow' }}>
            <img src="/logo192.png" alt="Taxi-Local.uk Logo" style={{ maxWidth: 150 }} />
          </div>
          <h3 className="text-center mb-3">Fare Calculation Estimate</h3>
          
          {licensingBody && (
            <Alert variant="info" className="mb-3 text-center">
              <strong>{licensingBody.fullName || licensingBody.name}</strong>
            </Alert>
          )}
          
          <p className="text-center"><strong>{startLocation}</strong> → <strong>{endLocation}</strong></p>
          <p className="text-center"><strong>Distance:</strong> {distance.toFixed(1)} miles</p>
          <p className="text-center"><strong>Duration:</strong> {Math.round(duration)} minutes</p>

          {fare && fare.length > 0 && fare.map((tariffFare, idx) => (
            <p className="text-center" key={idx}>
              <strong>{tariffFare.name}:</strong> £{tariffFare.price}
            </p>
          ))}

          <div className="d-grid gap-2 my-4">
            <button className="btn btn-primary" onClick={() => setShowLeadModal(true)}>
              Book Taxi Online
            </button>
            <button
              className="btn btn-success"
              onClick={() =>
                window.open(
                  `https://www.google.com/maps/dir/?api=1&origin=${encodeURIComponent(startLocation)}&destination=${encodeURIComponent(endLocation)}&travelmode=driving`,
                  '_blank'
                )
              }
            >
              Open in Google Maps
            </button>
            <button className="btn btn-secondary" onClick={resetAll}>
              Reset
            </button>
          </div>

          <Accordion flush>
            <Accordion.Item eventKey="0">
              <Accordion.Header>Tariffs Definition</Accordion.Header>
              <Accordion.Body>
                {licensingBody && licensingBody.tariffs && licensingBody.tariffs.map((t, idx) => (
                  <div key={idx} className="mb-2">
                    <strong>{t.name}:</strong> {t.description || `First mile: £${t.firstMile.toFixed(2)}, Subsequent: £${t.subsequentMile.toFixed(2)}`}
                  </div>
                ))}
              </Accordion.Body>
            </Accordion.Item>
            <Accordion.Item eventKey="1">
              <Accordion.Header>Disclaimer</Accordion.Header>
              <Accordion.Body>
                The fares calculated by this tool are estimates based on the shortest driving distance and standard
                tariffs provided by the licensing authority. Actual fares may vary due to traffic, detours, waiting times,
                tolls, or other surcharges. Taxi-Local does not accept any responsibility for Fares or Payments to the Operator.
              </Accordion.Body>
            </Accordion.Item>
          </Accordion>
        </>
      )}

      {/* LeadForm Modal */}
      <Modal show={showLeadModal} onHide={() => setShowLeadModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Taxi Request</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <LeadForm
            from={startLocation}
            to={endLocation}
            onClose={() => setShowLeadModal(false)}
          />
        </Modal.Body>
      </Modal>

      {/* Manual Mileage Input Modal */}
      <Modal show={showMileageModal} onHide={() => setShowMileageModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Enter Journey Distance</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-3">
            <h5>Journey Details:</h5>
            <p><strong>From:</strong> {startLocation}</p>
            <p><strong>To:</strong> {endLocation}</p>
            {licensingBody && (
              <p><strong>Licensing Authority:</strong> {licensingBody.name}</p>
            )}
          </div>
          
          <div className="mb-3">
            <label htmlFor="manualMileage" className="form-label">
              Distance in miles:
            </label>
            <input
              type="number"
              id="manualMileage"
              className="form-control"
              value={manualMileage}
              onChange={(e) => setManualMileage(e.target.value)}
              placeholder="Enter distance in miles"
              min="0.1"
              max="500"
              step="0.1"
            />
            <div className="form-text">
              Estimated distance: {calculateEstimatedDistance(startLocation, endLocation)} miles
            </div>
          </div>

          <div className="alert alert-info">
            <small>
              <strong>Note:</strong> This is an estimate for fare calculation purposes. 
              Actual distance may vary based on the specific route taken.
            </small>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowMileageModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleManualMileageCalculation}>
            Calculate Fare
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
